"use client";

import type React from "react";

import { useForm, FormProvider, type FieldErrors, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { schema, type OrganizationSchema } from "./schema/schema";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import type { OrganizationsInput, OrganizationsOutput } from "@/interfaces";
import {
  Building2,
  Search,
  MapPin,
  Mail,
  Phone,
  FileText,
  Upload,
  Globe,
  Instagram,
  Facebook,
  Twitter,
  Music2,
} from "lucide-react";
import InputMask from "react-input-mask";
import { OrganizationService } from "@/services/OrganizationService";
import { fetchAddressByZipCode } from "@/util/fetchCep";
import { CpfCnpjInput } from "@/components/CpfCnpjInput";
import { ImageUpload } from "@/components/ImageUpload";
import { FileService } from "@/services/FileService";
import { FilePurpose } from "@/interfaces/files";
import { Separator } from "@/components/ui/separator";
import { PhoneInputFields } from "@/components/PhoneNumberInput";

export default function OrganizationForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCepLoading, setIsCepLoading] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [initialImage, setInitialImage] = useState<string | undefined>(
    undefined
  );
  const [loadedFileId, setLoadedFileId] = useState<string | undefined>(
    undefined
  );

  const [activeTab, setActiveTab] = useState("general");

  const form = useForm<OrganizationSchema, undefined, OrganizationSchema>({
    resolver: zodResolver(schema) as Resolver<OrganizationSchema, undefined, OrganizationSchema>,
    defaultValues: {
      name: "",
      email: "",
      document: "",
      phone: {
        internationalCode: "55", // Updated default
        ddd: "",
        phoneNumber: "",
      },
      address: {
        zipCode: "",
        city: "",
        neighborhood: "",
        line1: "",
        line2: "",
        number: "",
      },
      fileId: "",
      website: "",
      socialLinks: {
        x: "",
        instagram: "",
        tiktok: "",
        facebook: "",
        googleProfile: "",
      },
    },
  });

  useEffect(() => {
    const service = new OrganizationService();

    const fetchInitialData = async () => {
      if (isEditing && id) {
        setIsLoading(true);
        try {
          const fetchedOrganization = (await service.findById(id, {
            expand: "file",
          })) as OrganizationsOutput;

          // Assuming fetchedOrganization.internationalCode is "55" or "1" (no "+")
          form.reset({
            name: fetchedOrganization.name,
            email: fetchedOrganization.email || "",
            document: fetchedOrganization.document || "",
            website: fetchedOrganization.website || "",
            socialLinks: {
              x: fetchedOrganization.socialLinks?.x || "",
              instagram: fetchedOrganization.socialLinks?.instagram || "",
              tiktok: fetchedOrganization.socialLinks?.tiktok || "",
              facebook: fetchedOrganization.socialLinks?.facebook || "",
              googleProfile:
                fetchedOrganization.socialLinks?.googleProfile || "",
            },
            phone: {
              internationalCode: fetchedOrganization.internationalCode || "55", // Default to "55" if API returns null/undefined
              ddd: fetchedOrganization.ddd || "",
              phoneNumber: fetchedOrganization.phoneNumber || "",
            },
            address: fetchedOrganization.address
              ? {
                  zipCode: fetchedOrganization.address.zipCode || "",
                  city: fetchedOrganization.address.city || "",
                  neighborhood: fetchedOrganization.address.neighborhood || "",
                  line1: fetchedOrganization.address.line1 || "",
                  line2: fetchedOrganization.address.line2 || "",
                  number: fetchedOrganization.address.number || "",
                }
              : {
                  zipCode: "",
                  city: "",
                  neighborhood: "",
                  line1: "",
                  line2: "",
                  number: "",
                },
            fileId: fetchedOrganization.fileId || "",
          });

          setLoadedFileId(fetchedOrganization.fileId || undefined);

          if (fetchedOrganization.file?.url) {
            setInitialImage(fetchedOrganization.file.url);
          }
        } catch (error) {
          console.error("Failed to fetch organization data:", error);
        } finally {
          setIsLoading(false);
        }
      } else {
        form.reset({
          ...form.formState.defaultValues,
          phone: {
            // Ensure default phone object is set for new forms
            internationalCode: "55",
            ddd: "",
            phoneNumber: "",
          },
          address: form.formState.defaultValues?.address || {
            zipCode: "",
            city: "",
            neighborhood: "",
            line1: "",
            line2: "",
            number: "",
          },
        });
      }
    };

    setIsEditing(!!id);
    fetchInitialData();
  }, [id, form, isEditing]);

  async function onSubmit(values: OrganizationSchema) {
    setIsLoading(true);
    const service = new OrganizationService();
    const filesService = new FileService();
    let finalFileId: string | undefined;

    if (selectedFile) {
      // New file selected, upload and use its ID
      try {
        const fileInput = {
          file: selectedFile,
          purpose: FilePurpose.ORGANIZATIONS,
        };
        const uploadedFile = await filesService.upload(fileInput);
        finalFileId = uploadedFile.id;
      } catch (error) {
        console.error("File upload failed:", error);
        setIsLoading(false);
        return;
      }
    } else if (isEditing) {
      // No new file, and we are editing. Use the ID that was loaded with the organization.
      finalFileId = loadedFileId;
    } else {
      // No new file, and we are creating. fileId should be undefined or empty.
      // values.fileId would be "" from defaultValues if not touched.
      finalFileId = values.fileId || undefined;
    }

    const { phone, address, ...restOfValues } = values;

    const input: OrganizationsInput = {
      ...restOfValues, // This spread does not include 'fileId' from 'values'
      fileId: finalFileId, // Explicitly use the determined finalFileId
      name: values.name,
      email: values.email || undefined,
      document: values.document || undefined,
    };

    input.website = values.website?.trim() ? values.website.trim() : undefined;

    if (values.socialLinks) {
      const formattedSocialLinks = {
        x: values.socialLinks.x?.trim()
          ? values.socialLinks.x.trim()
          : undefined,
        instagram: values.socialLinks.instagram?.trim()
          ? values.socialLinks.instagram.trim()
          : undefined,
        tiktok: values.socialLinks.tiktok?.trim()
          ? values.socialLinks.tiktok.trim()
          : undefined,
        facebook: values.socialLinks.facebook?.trim()
          ? values.socialLinks.facebook.trim()
          : undefined,
        googleProfile: values.socialLinks.googleProfile?.trim()
          ? values.socialLinks.googleProfile.trim()
          : undefined,
      };

      input.socialLinks = Object.values(formattedSocialLinks).some(Boolean)
        ? formattedSocialLinks
        : undefined;
    } else {
      input.socialLinks = undefined;
    }

    if (address && address.zipCode) {
      input.address = {
        zipCode: address.zipCode,
        city: address.city,
        neighborhood: address.neighborhood,
        line1: address.line1,
        number: address.number,
        line2: address.line2 || undefined,
      };
    } else {
      input.address = undefined;
    }

    // Values from form (phone.internationalCode) will be "55" or "1"
    if (phone && phone.ddd && phone.phoneNumber && phone.internationalCode) {
      input.internationalCode = phone.internationalCode;
      input.ddd = phone.ddd;
      input.phoneNumber = phone.phoneNumber;
    } else {
      input.internationalCode = undefined;
      input.ddd = undefined;
      input.phoneNumber = undefined;
    }

    try {
      if (isEditing && id) {
        await service.update(id, input);
        navigate(`/organizacoes/${id}`);
      } else {
        await service.create(input);
        navigate("/organizacoes");
      }
    } catch (error) {
      console.error("Failed to save organization:", error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleError = (errors: FieldErrors<OrganizationSchema>) => {
    if (errors.address) {
      setActiveTab("address");
    } else {
      setActiveTab("general");
    }
  };

  const handleCepBlur = async (zipCode: string) => {
    if (!zipCode || zipCode.replace(/[^0-9]/g, "").length < 8) return;

    try {
      setIsCepLoading(true);
      const addressData = await fetchAddressByZipCode(zipCode);
      if (addressData) {
        form.setValue(`address.city`, addressData.localidade || "");
        form.setValue(`address.neighborhood`, addressData.bairro || "");
        form.setValue(`address.line1`, addressData.logradouro || "");
      }
    } catch (error) {
      console.error("Failed to fetch CEP data:", error);
    } finally {
      setIsCepLoading(false);
    }
  };

  const isFetchingData = isLoading && isEditing && !!id;
  if (isFetchingData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <div className="p-4 max-w-5xl mx-auto">
        <form onSubmit={form.handleSubmit(onSubmit, handleError)}>
          <Card className="overflow-hidden border-none shadow-md">
            <CardHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white py-3 px-4">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                <CardTitle className="text-base">
                  {isEditing ? "Editar Organização" : "Nova Organização"}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full"
              >
                <TabsList className="w-full justify-start rounded-none border-b bg-transparent h-auto p-0">
                  <TabsTrigger
                    value="general"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#EE7B11] data-[state=active]:bg-transparent data-[state=active]:text-[#EE7B11] py-3 px-4 text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-[#EE7B11]" />
                      Informações Gerais
                    </div>
                  </TabsTrigger>
                  <TabsTrigger
                    value="address"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#EE7B11] data-[state=active]:bg-transparent data-[state=active]:text-[#EE7B11] py-3 px-4 text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-[#EE7B11]" />
                      Endereço
                    </div>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="general" className="p-4 space-y-4">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="md:w-1/4">
                      <div className="bg-gray-50 p-3 rounded-lg flex flex-col items-center justify-center">
                        <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                          <Upload className="h-4 w-4 text-[#EE7B11]" />
                          Logo
                        </h3>
                        <ImageUpload
                          initialImage={initialImage}
                          onImageChange={(file) => setSelectedFile(file)}
                        />
                      </div>
                    </div>
                    <div className="md:w-3/4 space-y-4">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2 text-sm">
                              <Building2 className="h-4 w-4 text-[#EE7B11]" />
                              Nome da organização
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Nome da organização"
                                {...field}
                                className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] h-9 text-sm"
                              />
                            </FormControl>
                            <FormMessage className="text-sm" />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2 text-sm">
                                <Mail className="h-4 w-4 text-[#EE7B11]" />
                                Email
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="email@example.com"
                                  {...field}
                                  className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] h-9 text-sm"
                                />
                              </FormControl>
                              <FormMessage className="text-sm" />
                            </FormItem>
                          )}
                        />

                        <div className="space-y-2">
                          <FormLabel className="flex items-center gap-2 text-sm">
                            <Phone className="h-4 w-4 text-[#EE7B11]" />
                            Telefone
                          </FormLabel>
                          <PhoneInputFields basePath="phone" />
                          {form.formState.errors.phone?.internationalCode
                            ?.message && (
                            <FormMessage className="text-sm">
                              {
                                form.formState.errors.phone.internationalCode
                                  .message
                              }
                            </FormMessage>
                          )}
                          {form.formState.errors.phone?.ddd?.message && (
                            <FormMessage className="text-sm">
                              {form.formState.errors.phone.ddd.message}
                            </FormMessage>
                          )}
                          {form.formState.errors.phone?.phoneNumber
                            ?.message && (
                            <FormMessage className="text-sm">
                              {form.formState.errors.phone.phoneNumber.message}
                            </FormMessage>
                          )}
                          {form.formState.errors.phone?.root?.message && (
                            <FormMessage className="text-sm">
                              {form.formState.errors.phone.root.message}
                            </FormMessage>
                          )}
                          {form.formState.errors.phone?.ddd?.message &&
                            form.formState.errors.phone.ddd.message.includes(
                              "obrigatórios"
                            ) &&
                            !form.formState.errors.phone.ddd.message.includes(
                              "2 ou 3 dígitos"
                            ) && (
                              <FormMessage className="text-sm">
                                {form.formState.errors.phone.ddd.message}
                              </FormMessage>
                            )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="document"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2 text-sm">
                                <FileText className="h-4 w-4 text-[#EE7B11]" />
                                CPF/CNPJ
                              </FormLabel>
                              <FormControl>
                                <CpfCnpjInput
                                  value={field.value ?? ""}
                                  onChange={field.onChange}
                                  className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] h-9 text-sm"
                                />
                              </FormControl>
                              <FormMessage className="text-sm" />
                            </FormItem>
                          )}
                        />
                      </div>

                      <Separator />

                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                          <Globe className="h-4 w-4 text-[#EE7B11]" />
                          Presença online
                        </div>

                        <FormField
                          control={form.control}
                          name="website"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2 text-sm">
                                <Globe className="h-4 w-4 text-[#EE7B11]" />
                                Website
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value ?? ""}
                                  placeholder="https://suaempresa.com"
                                  className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] h-9 text-sm"
                                />
                              </FormControl>
                              <FormMessage className="text-sm" />
                            </FormItem>
                          )}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="socialLinks.x"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="flex items-center gap-2 text-sm">
                                  <Twitter className="h-4 w-4 text-[#EE7B11]" />
                                  X (antigo Twitter)
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    value={field.value ?? ""}
                                    placeholder="https://x.com/suaempresa"
                                    className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] h-9 text-sm"
                                  />
                                </FormControl>
                                <FormMessage className="text-sm" />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="socialLinks.instagram"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="flex items-center gap-2 text-sm">
                                  <Instagram className="h-4 w-4 text-[#EE7B11]" />
                                  Instagram
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    value={field.value ?? ""}
                                    placeholder="https://instagram.com/suaempresa"
                                    className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] h-9 text-sm"
                                  />
                                </FormControl>
                                <FormMessage className="text-sm" />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="socialLinks.facebook"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="flex items-center gap-2 text-sm">
                                  <Facebook className="h-4 w-4 text-[#EE7B11]" />
                                  Facebook
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    value={field.value ?? ""}
                                    placeholder="https://facebook.com/suaempresa"
                                    className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] h-9 text-sm"
                                  />
                                </FormControl>
                                <FormMessage className="text-sm" />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="socialLinks.tiktok"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="flex items-center gap-2 text-sm">
                                  <Music2 className="h-4 w-4 text-[#EE7B11]" />
                                  TikTok
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    value={field.value ?? ""}
                                    placeholder="https://tiktok.com/@suaempresa"
                                    className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] h-9 text-sm"
                                  />
                                </FormControl>
                                <FormMessage className="text-sm" />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="socialLinks.googleProfile"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="flex items-center gap-2 text-sm">
                                  <Search className="h-4 w-4 text-[#EE7B11]" />
                                  Google Empresas
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    value={field.value ?? ""}
                                    placeholder="https://business.google.com/..."
                                    className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] h-9 text-sm"
                                  />
                                </FormControl>
                                <FormMessage className="text-sm" />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="address" className="p-4 space-y-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <FormField
                      control={form.control}
                      name={`address.zipCode`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2 text-sm">
                            <MapPin className="h-4 w-4 text-[#EE7B11]" />
                            CEP
                          </FormLabel>
                          <div className="flex gap-2">
                            <FormControl>
                              <InputMask
                                mask="99999-999"
                                value={field.value || ""}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                              >
                                {(
                                  inputProps: React.InputHTMLAttributes<HTMLInputElement>
                                ) => (
                                  <Input
                                    {...inputProps}
                                    placeholder="99999-999"
                                    className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] h-9 text-sm"
                                  />
                                )}
                              </InputMask>
                            </FormControl>
                            <Button
                              type="button"
                              onClick={() => handleCepBlur(field.value || "")}
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2 border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10 h-9"
                              disabled={isCepLoading}
                            >
                              {isCepLoading ? (
                                <span className="h-4 w-4 border-2 border-[#EE7B11] border-t-transparent rounded-full animate-spin"></span>
                              ) : (
                                <Search className="h-4 w-4" />
                              )}
                              Buscar
                            </Button>
                          </div>
                          <FormMessage className="text-sm" />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      control={form.control}
                      name={`address.city`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm">Cidade</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Cidade"
                              {...field}
                              className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] h-9 text-sm"
                            />
                          </FormControl>
                          <FormMessage className="text-sm" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`address.neighborhood`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm">Bairro</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Bairro"
                              {...field}
                              className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] h-9 text-sm"
                            />
                          </FormControl>
                          <FormMessage className="text-sm" />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator className="my-2" />

                  <div className="grid grid-cols-4 gap-3">
                    <div className="col-span-3">
                      <FormField
                        control={form.control}
                        name={`address.line1`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm">Endereço</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Endereço"
                                {...field}
                                className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] h-9 text-sm"
                              />
                            </FormControl>
                            <FormMessage className="text-sm" />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name={`address.number`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm">Número</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="123"
                              {...field}
                              className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] h-9 text-sm"
                            />
                          </FormControl>
                          <FormMessage className="text-sm" />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name={`address.line2`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">Complemento</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="apto 123"
                            {...field}
                            className="border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11] h-9 text-sm"
                          />
                        </FormControl>
                        <FormMessage className="text-sm" />
                      </FormItem>
                    )}
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
            <CardFooter className="border-t p-4 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  id
                    ? navigate(`/organizacoes/${id}`)
                    : navigate("/organizacoes")
                }
                className="border-gray-200 text-gray-700 hover:bg-gray-50 h-9 text-sm"
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                size="sm"
                className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white h-9 text-sm"
                disabled={isLoading}
              >
                {isLoading ? (
                  <LoadingSpinner className="h-4 w-4" />
                ) : isEditing ? (
                  "Atualizar"
                ) : (
                  "Cadastrar"
                )}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </div>
    </FormProvider>
  );
}








